/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  isEmptyOrNull,
  SLRDropdownForm,
  SLRFormInfo,
  SLRGeoAreaForm
} from '@SLR/shared-library';
import { CONTACT_URL, OnboardingType } from 'configs';
import { CUSTOM_ONBOARDING_TYPES } from 'configs/onboarding';
import { FC } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  DESCRIPTION_MAX_CHAR_LENGTH,
  OrganizationDraftInput,
  SOLUTION_INFORMATION_MAX_CHAR_LENGTH,
  URL_MAX_CHAR_LENGTH
} from '../schema/organizationDraft';
import { MemberForm } from './MemberForm';

type Props = {
  onboardingType: OnboardingType;
  isError?: boolean;
};

export const DraftForm: FC<Props> = ({ onboardingType, isError = false }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'draftPage'
  });

  const {
    formState: { errors },
    setValue,
    control,
    watch,
    register
  } = useFormContext<OrganizationDraftInput>();

  const solutionInformationCount = watch('solutionInformation')?.length ?? 0;
  const websiteCount = watch('website')?.length ?? 0;
  const descriptionCount = watch('description')?.length ?? 0;

  return (
    <Col xs="12">
      <Row className="gap-4">
        <Col xs="12" className={errors.name ? 'text-danger' : ''}>
          <Form.Label htmlFor="name">
            {t(`name.${onboardingType}.label`)}
          </Form.Label>
          {onboardingType === OnboardingType.MUNICIPALITY ? (
            <SLRGeoAreaForm
              id="name"
              control={control}
              name="geoAreaId"
              isInvalid={!!errors.name}
              onSelect={(g) =>
                g ? setValue('name', g.name) : setValue('name', null)
              }
              {...t('name.municipality', { returnObjects: true })}
            />
          ) : (
            <Form.Control
              id="name"
              type="text"
              className="form-control-lg"
              placeholder={t(`name.${onboardingType}.placeholder`)}
              isInvalid={!!errors.name}
              {...register('name')}
            />
          )}
          <SLRFormInfo isInvalid={!!errors.name} text={errors.name?.message} />
          {isError && onboardingType === OnboardingType.MUNICIPALITY && (
            <p className="fs-6 mt-1 text-danger w-100 slr-form-info">
              <Trans
                t={t}
                i18nKey={`name.${onboardingType}.error`}
                components={[
                  <Link target="_blank" to={CONTACT_URL} key="CONTACT_URL" />
                ]}
              />
            </p>
          )}
        </Col>
        <Col xs="12" className={errors.description ? 'text-danger' : ''}>
          <Form.Label htmlFor="description">
            {t(`description.label.${onboardingType}`)}
          </Form.Label>

          <Form.Control
            id="description"
            type="text"
            as="textarea"
            rows={4}
            className="form-control-lg"
            placeholder={t('description.placeholder')}
            isInvalid={!!errors.description}
            {...register('description')}
          />

          <SLRFormInfo
            isInvalid={!!errors.description}
            text={errors.description?.message}
            counter={{
              count: descriptionCount,
              maxCount: DESCRIPTION_MAX_CHAR_LENGTH
            }}
          />
        </Col>
        {onboardingType === OnboardingType.PUBLIC_AUTHORITIES && (
          <Col xs="12" className={errors.type ? 'text-danger' : ''}>
            <Form.Label htmlFor="additionalInformation">
              {t('additionalInformation.label')}
            </Form.Label>
            <SLRDropdownForm
              id="additionalInformation"
              control={control}
              name="additionalInformation"
              placeholder={t('additionalInformation.placeholder')}
              isInvalid={!!errors?.type}
              options={CUSTOM_ONBOARDING_TYPES.map((orgType) => ({
                value: orgType,
                label: orgType
              }))}
            />
            <SLRFormInfo
              isInvalid={!!errors.type}
              text={errors.type?.message}
            />
          </Col>
        )}
        {onboardingType === OnboardingType.SOLUTION_PROVIDER && (
          <Col
            xs="12"
            className={errors.solutionInformation ? 'text-danger' : ''}
          >
            <Form.Label htmlFor="solutionInformation">
              {t('solutionInformation.label')}
            </Form.Label>
            <Form.Control
              id="solutionInformation"
              type="text"
              className="form-control-lg"
              placeholder={t('solutionInformation.placeholder')}
              isInvalid={!!errors.solutionInformation}
              {...register('solutionInformation')}
            />
            <SLRFormInfo
              isInvalid={!!errors.solutionInformation}
              text={errors.solutionInformation?.message}
              counter={{
                count: solutionInformationCount,
                maxCount: SOLUTION_INFORMATION_MAX_CHAR_LENGTH
              }}
            />
          </Col>
        )}
        {onboardingType === OnboardingType.SOLUTION_PROVIDER && (
          <Col xs="12" className={errors.website ? 'text-danger' : ''}>
            <Form.Label htmlFor="website">{t('website.label')}</Form.Label>
            <Form.Control
              id="website"
              type="text"
              className="form-control-lg"
              placeholder={t('website.placeholder')}
              isInvalid={!!errors.website}
              {...register('website', {
                setValueAs: (value: string) =>
                  !isEmptyOrNull(value) && !value.startsWith('http')
                    ? 'https://' + value
                    : value
              })}
            />
            <SLRFormInfo
              isInvalid={!!errors.website}
              text={errors?.website?.message}
              counter={{
                count: websiteCount,
                maxCount: URL_MAX_CHAR_LENGTH
              }}
            />
          </Col>
        )}
        <Col xs="12" className={errors.contactPerson ? 'text-danger' : ''}>
          <Form.Label htmlFor="contactPerson">
            {t('contactPerson.label')}
          </Form.Label>
          <Form.Control
            id="contactPerson"
            type="text"
            className="form-control-lg"
            placeholder={t('contactPerson.placeholder')}
            isInvalid={!!errors.contactPerson}
            {...register('contactPerson')}
          />

          <SLRFormInfo
            isInvalid={!!errors.contactPerson}
            text={errors?.contactPerson?.message}
          />
        </Col>
        <Col
          xs="12"
          className={errors.contactEmailAddress ? 'text-danger' : ''}
        >
          <Form.Label htmlFor="contactEmailAddress">
            {t('contactEmailAddress.label')}
          </Form.Label>
          <SLRFormInfo
            text={t(`contactEmailAddress.info.${onboardingType}`)}
            className="pb-3"
          />

          <Form.Control
            id="contactEmailAddress"
            type="text"
            className="form-control-lg"
            placeholder={t('contactEmailAddress.placeholder')}
            isInvalid={!!errors.contactEmailAddress}
            {...register('contactEmailAddress')}
          />

          <SLRFormInfo
            isInvalid={!!errors.contactEmailAddress}
            text={errors?.contactEmailAddress?.message}
          />
        </Col>
        <Col xs="12" className={errors.emails ? 'text-danger' : ''}>
          <Form.Label htmlFor="emails">
            {t('memberEmailAddresses.label')}
          </Form.Label>

          <MemberForm />
        </Col>
      </Row>
    </Col>
  );
};
